import { CHART_COLORS } from 'consts/consts';
import { type ColorStop, getColor } from 'utils/util/color';
import {
  type SurveyChartBucket,
  type SurveyChartSeries,
} from 'views/Administration/CustomSurveys/consts';
import {
  GRAY_DARK,
  GRAY_LIGHT,
  GREEN_DARK,
  LIKERT_SCALE,
  RED_DARK,
} from 'views/Widgets/Charts/chartColors';

// Shared percent formatting - the same "${value}%" shape is used for chart.js
// tick labels and tooltip text across every distribution/trend chart in
// SurveyChartView.tsx.
export const formatPercentTick = (value: number) => `${value}%`;

// Red (unfavorable) -> gray (neutral) -> green (favorable), interpolated
// across a series's positions in [0, 1]. Used for STAR_RATING only - unlike
// LIKERT (always a fixed 5-point scale, see LikertScaleInput's default
// scale), a star rating's point count varies per question, so there's no
// fixed discrete palette to key off like LIKERT_SCALE below.
const SENTIMENT_COLOR_SCALE: ColorStop[] = [
  { value: 0, color: RED_DARK },
  { value: 0.5, color: GRAY_LIGHT },
  { value: 1, color: GREEN_DARK },
];

// The same scale for trend lines, with a darker neutral: a thin line in the
// light gray - or in a blend halfway to it - is close to invisible on white.
const LINE_SENTIMENT_COLOR_SCALE: ColorStop[] = [
  { value: 0, color: RED_DARK },
  { value: 0.5, color: GRAY_DARK },
  { value: 1, color: GREEN_DARK },
];

const getStarRatingGradientColors = (
  series: SurveyChartSeries[],
  scale: ColorStop[]
): string[] =>
  series.length > 1
    ? series.map((_, i) => getColor(i / (series.length - 1), scale))
    : series.map(() => scale[1].color);

// LIKERT's discrete 5-color palette (Strongly agree -> Strongly disagree) -
// the same one already established for Likert scales in the engagement
// heatmap (see LikertHeatmap.LIKERT_SCALE), reused here rather than
// inventing a separate continuous gradient, so a Likert response reads the
// same way in every chart across the app. Falls back to gray for a
// value_key LIKERT_SCALE doesn't recognize (shouldn't normally happen,
// since LIKERT is always the same fixed 5-point scale).
export const getLikertScaleColors = (series: SurveyChartSeries[]): string[] =>
  series.map(
    (s) =>
      LIKERT_SCALE.find((point) => point.value === Number(s.value_key))
        ?.color ?? GRAY_LIGHT
  );

// Color a question's chart series to match its scale, instead of assigning
// arbitrary positions from the categorical CHART_COLORS palette - each
// chartable type with a natural scale gets its own branch below. Anything
// else (MULTIPLE_CHOICE, DROPDOWN, ...) has no inherent ordering, so it falls
// through to that categorical palette.
export const getSeriesColors = (
  questionType: string,
  series: SurveyChartSeries[]
): string[] => {
  switch (questionType) {
    case 'SWITCH':
      // [true, false] (Yes first - see get_value_domain) isn't a range, so
      // colors are assigned by key instead of position.
      return series.map((s) =>
        s.value_key === 'true' ? GREEN_DARK : RED_DARK
      );
    case 'LIKERT':
      return getLikertScaleColors(series);
    case 'STAR_RATING':
      return getStarRatingGradientColors(series, SENTIMENT_COLOR_SCALE);
    default:
      return series.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
  }
};

// getSeriesColors for a trend's lines, where the neutral gray has to be
// dark enough to see as a stroke rather than a filled slice.
export const getLineSeriesColors = (
  questionType: string,
  series: SurveyChartSeries[]
): string[] =>
  questionType === 'STAR_RATING'
    ? getStarRatingGradientColors(series, LINE_SENTIMENT_COLOR_SCALE)
    : getSeriesColors(questionType, series).map((color) =>
        color === GRAY_LIGHT ? GRAY_DARK : color
      );

// Every statement shares the same "shown" denominator (see
// shared_denominator in custom_survey_analytics.py - a respondent answers
// every statement in one sitting, so "shown the question" counts once per
// person regardless of how many statements it has), but "answered" can
// genuinely differ per statement: when the whole multi-statement question
// isn't required, a respondent can save having filled in some statements
// and left others blank (see LikertScaleInput's required-validation, which
// only enforces all-or-nothing when the question itself is required) - so
// each statement needs its own honest count, not one collapsed/approximated
// number for the whole block.
export const statementRespondentCount = (
  bucket: SurveyChartBucket,
  series: SurveyChartSeries[],
  i: number
): { denominator: number; answered: number } => ({
  denominator: bucket.denominator,
  answered: series.reduce((sum, s) => sum + (s.counts[i] ?? 0), 0),
});

// When every statement happens to have the same denominator/answered (the
// common case - respondents usually answer every statement in one sitting),
// repeating the identical caption under each one is just noise, so show it
// once for the whole block instead. The moment even one differs (or is
// suppressed - see _suppress_low_response_buckets in
// custom_survey_analytics.py, which zeroes a suppressed bucket's counts),
// this returns null and each statement keeps its own honest count.
export const collapsedRespondentCount = (
  counts: { denominator: number; answered: number }[]
): { denominator: number; answered: number } | null => {
  const [first, ...rest] = counts;
  if (!first) return null;
  return rest.every(
    (c) => c.denominator === first.denominator && c.answered === first.answered
  )
    ? first
    : null;
};
